// Pre-built RFP Templates for common vendor evaluation scenarios

export interface RFPTemplate {
  id: string;
  name: string;
  description: string;
  categories: string[];
  priorities: string[];
  questionTemplate: string;
  dealBreakerQuestionIds: string[];
  estimatedDuration: string; // e.g., "2-3 weeks"
}

export const RFP_TEMPLATES: RFPTemplate[] = [
  {
    id: 'saas-security-audit',
    name: 'SaaS Security Audit',
    description: 'Comprehensive security evaluation for SaaS vendors. Focuses on compliance, data protection, and security practices.',
    categories: [
      'Security & Compliance',
      'Company & Stability',
      'Data Protection',
      'AI & Emerging Technology',
    ],
    priorities: ['Critical', 'High'],
    questionTemplate: 'SaaS',
    dealBreakerQuestionIds: [
      'sec-001', // SOC 2
      'sec-002', // GDPR
      'sec-004', // Encryption
      'ai-005', // AI training data
      'ai-006', // Data leakage
    ],
    estimatedDuration: '2-3 weeks',
  },
  {
    id: 'api-integration-vendor',
    name: 'API/Integration Platform',
    description: 'Technical evaluation for API platforms and integration tools. Emphasizes performance, reliability, and developer experience.',
    categories: [
      'Technical Capabilities',
      'Performance & Scalability',
      'Support & SLAs',
      'Security & Compliance',
    ],
    priorities: ['Critical', 'High'],
    questionTemplate: 'SaaS',
    dealBreakerQuestionIds: [
      'tech-001', // Architecture
      'tech-005', // API response times
      'tech-006', // Uptime
      'tech-007', // APIs provided
    ],
    estimatedDuration: '3-4 weeks',
  },
  {
    id: 'cloud-infrastructure',
    name: 'Cloud Infrastructure Provider',
    description: 'Evaluation for cloud hosting, infrastructure, and platform providers. Focus on reliability, scalability, and operational excellence.',
    categories: [
      'Technical Capabilities',
      'Performance & Scalability',
      'Security & Compliance',
      'Disaster Recovery & Business Continuity',
      'Pricing & Contract',
    ],
    priorities: ['Critical', 'High', 'Medium'],
    questionTemplate: 'Infrastructure',
    dealBreakerQuestionIds: [
      'tech-006', // Uptime
      'dr-001', // DR plan
      'dr-004', // RTO/RPO
      'perf-001', // Load testing
    ],
    estimatedDuration: '4-6 weeks',
  },
  {
    id: 'consulting-firm',
    name: 'Consulting Firm Evaluation',
    description: 'Assess consulting firms for strategic projects. Focuses on expertise, delivery track record, and team quality.',
    categories: [
      'Company & Stability',
      'Technical Capabilities',
      'References',
      'Pricing & Contract',
      'Implementation & Migration',
    ],
    priorities: ['Critical', 'High'],
    questionTemplate: 'Consulting',
    dealBreakerQuestionIds: [
      'ref-001', // Customer references
      'ref-002', // Case studies
      'comp-007', // Customer retention
    ],
    estimatedDuration: '2-3 weeks',
  },
  {
    id: 'data-analytics-platform',
    name: 'Data Warehouse/Analytics Platform',
    description: 'Evaluation for data warehouses, BI tools, and analytics platforms. Emphasizes data governance, performance, and AI capabilities.',
    categories: [
      'Technical Capabilities',
      'AI & Emerging Technology',
      'Security & Compliance',
      'Performance & Scalability',
      'Support & SLAs',
    ],
    priorities: ['Critical', 'High'],
    questionTemplate: 'SaaS',
    dealBreakerQuestionIds: [
      'tech-009', // Data residency
      'tech-010', // Data retention
      'ai-005', // AI training data
      'perf-001', // Load testing
    ],
    estimatedDuration: '3-5 weeks',
  },
  {
    id: 'payment-processor',
    name: 'Payment Processor',
    description: 'Evaluation for payment gateways and financial service providers. Focus on security, compliance, and transaction reliability.',
    categories: [
      'Security & Compliance',
      'Technical Capabilities',
      'Pricing & Contract',
      'Support & SLAs',
      'Performance & Scalability',
    ],
    priorities: ['Critical', 'High'],
    questionTemplate: 'Payments',
    dealBreakerQuestionIds: [
      'sec-001', // SOC 2
      'sec-003', // HIPAA (if healthcare)
      'tech-006', // Uptime
      'price-001', // Pricing model
    ],
    estimatedDuration: '4-6 weeks',
  },
  {
    id: 'healthcare-saas',
    name: 'Healthcare SaaS Platform',
    description: 'Evaluation for healthcare technology vendors. Strict focus on HIPAA compliance, data security, and reliability.',
    categories: [
      'Security & Compliance',
      'Technical Capabilities',
      'Support & SLAs',
      'Disaster Recovery & Business Continuity',
      'AI & Emerging Technology',
    ],
    priorities: ['Critical', 'High'],
    questionTemplate: 'Healthcare',
    dealBreakerQuestionIds: [
      'sec-001', // SOC 2
      'sec-003', // HIPAA
      'ai-005', // AI training data
      'ai-006', // Data leakage
      'dr-001', // DR plan
    ],
    estimatedDuration: '5-8 weeks',
  },
  {
    id: 'ai-ml-platform',
    name: 'AI/ML Platform',
    description: 'Evaluation for AI and machine learning platforms. Deep dive into AI capabilities, ethics, data privacy, and model governance.',
    categories: [
      'AI & Emerging Technology',
      'Security & Compliance',
      'Technical Capabilities',
      'Performance & Scalability',
    ],
    priorities: ['Critical', 'High'],
    questionTemplate: 'SaaS',
    dealBreakerQuestionIds: [
      'ai-003', // AI models disclosed
      'ai-004', // AI accuracy
      'ai-005', // Training data
      'ai-006', // Data leakage
      'ai-015', // AI compliance
    ],
    estimatedDuration: '4-6 weeks',
  },
  {
    id: 'enterprise-comprehensive',
    name: 'Enterprise Comprehensive RFP',
    description: 'Full enterprise vendor evaluation covering all aspects. Most thorough assessment for mission-critical vendor selection.',
    categories: [
      'Company & Stability',
      'Technical Capabilities',
      'Security & Compliance',
      'Support & SLAs',
      'Pricing & Contract',
      'Implementation & Migration',
      'Product Roadmap',
      'References',
      'Disaster Recovery & Business Continuity',
      'Performance & Scalability',
      'Vendor Management',
      'AI & Emerging Technology',
    ],
    priorities: ['Critical', 'High', 'Medium'],
    questionTemplate: 'All',
    dealBreakerQuestionIds: [
      'sec-001', // SOC 2
      'tech-006', // Uptime
      'comp-007', // Customer retention
      'dr-001', // DR plan
    ],
    estimatedDuration: '8-12 weeks',
  },
  {
    id: 'quick-vendor-screening',
    name: 'Quick Vendor Screening',
    description: 'Rapid initial screening for vendor shortlisting. Focuses on critical must-haves before deeper evaluation.',
    categories: [
      'Company & Stability',
      'Security & Compliance',
      'Pricing & Contract',
      'References',
    ],
    priorities: ['Critical'],
    questionTemplate: 'All',
    dealBreakerQuestionIds: [
      'comp-001', // Years in business
      'sec-001', // SOC 2
      'ref-001', // References
    ],
    estimatedDuration: '1 week',
  },
];
